import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { CloseOutlined, LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button, Modal, Pagination, Spin,
} from "antd";
import { toast } from "react-toastify";

import NoResultsGif from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import SwabListExport from "./SwabListExport";
import SwabListTable from "./SwabListTable";

import { analyticsSampleDataFetch } from "../../../../../actions/envAnalytics";
import {
  CATEGORY_API_PARAMS,
  normalizeAnalyticsSampleRows,
  parseDisplayDateRange,
} from "../../utils";

import pumpkinGif from "../../../../../assets/images/environment/pumpkin.gif";
import "./SwabList.css";

const CATEGORY_TITLES = {
  out_of_spec: "Out of Spec",
  in_spec: "In Spec",
  incomplete: "Incomplete Specs",
  samples_received: "Samples Received",
  samples_in_testing: "Samples in Testing",
  results_available: "Test Results Available",
};

const TESTS_COLUMN_LABELS = {
  out_of_spec: "Tests Out of Spec",
  in_spec: "Tests In Spec",
  incomplete: "Incomplete Tests",
  samples_received: "Tests",
  samples_in_testing: "Tests",
  results_available: "Tests",
};

const TABLE_HEADERS = ["Sample ID", "Received date", "Sample Type"];

export default function SwabList({
  open,
  onClose,
  category,
  dateRange = "",
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const [swabList, setSwabList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const abortControllerRef = useRef(null);

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return swabList.slice(start, start + pageSize);
  }, [swabList, currentPage, pageSize]);

  const apiParams = CATEGORY_API_PARAMS[category];
  const usesApiFetch = Boolean(apiParams);
  const modalTitle = `Sample List: ${CATEGORY_TITLES[category]}`;
  const testsColumnLabel = TESTS_COLUMN_LABELS[category];

  useEffect(() => {
    if (!open || !usesApiFetch || !dateRange) {
      return undefined;
    }

    const fetchSwabList = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      setIsLoading(true);

      try {
        const { date_from: dateFrom, date_to: dateTo } = parseDisplayDateRange(dateRange);
        const response = await analyticsSampleDataFetch(
          {
            date_from: dateFrom,
            date_to: dateTo,
            ...apiParams,
          },
          abortControllerRef.current.signal,
        );

        if (abortControllerRef.current.signal.aborted) return;

        if (response.success) {
          setSwabList(normalizeAnalyticsSampleRows(response.result, category));
        } else if (!response.canceled) {
          setSwabList([]);
          toast.error("Failed to load swab list");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          toast.error("Error loading swab list");
          setSwabList([]);
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchSwabList();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [open, category, dateRange, apiParams, usesApiFetch]);

  useEffect(() => {
    if (!open) {
      setSwabList([]);
    }
  }, [open]);

  useEffect(() => {
    setCurrentPage(1);
  }, [swabList]);

  const handleExport = () => {
    setExportOpen(true);
  };

  const handleCloseExport = () => {
    setExportOpen(false);
  };

  return (
    <>
    <Modal
      open={open}
      closable={false}
      centered
      footer={null}
      onCancel={onClose}
      className="SwabList"
      width="960px"
      destroyOnHidden={false}
    >
      <div className="SwabList__Header">
        <span className="SwabList__HeaderTitle">{modalTitle}</span>
        <CloseOutlined
          className="SwabList__CloseIcon"
          aria-label="Close"
          onClick={onClose}
        />
      </div>
      <div className="SwabList__Toolbar">
        <span className="SwabList__DateRange">
          Date Range :
          {" "}
          <span className="SwabList__DateRangeValue">{dateRange || "-"}</span>
        </span>
        <Button
          type="default"
          className="SwabList__ExportButton"
          icon={<UploadOutlined />}
          onClick={handleExport}
          disabled={isLoading || swabList.length === 0}
        >
          Export
        </Button>
      </div>
      <div className="SwabList__Content">
        <StyledScrollbar className="SwabList__ContentScrollbar">
          {isLoading ? (
            <div className="SwabList__Loading">
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : swabList.length > 0 ? (
            <SwabListTable
              data={paginatedList}
              testsColumnLabel={testsColumnLabel}
              scrollY={false}
            />
          ) : (
            <NoResultsGif message="No Data" image={pumpkinGif} className="SwabList__NoResults" />
          )}
        </StyledScrollbar>
        {!isLoading && swabList.length > 0 && (
          <Pagination
            className="SwabList__Pagination"
            current={currentPage}
            pageSize={pageSize}
            total={swabList.length}
            size="small"
            showSizeChanger
            pageSizeOptions={[10]}
            hideOnSinglePage
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
          />
        )}
      </div>
    </Modal>
    <SwabListExport
      open={exportOpen}
      onClose={handleCloseExport}
      swabList={swabList}
      tableHeaders={TABLE_HEADERS}
      testsColumnLabel={testsColumnLabel}
      modalTitle={modalTitle}
      dateRange={dateRange}
    />
    </>
  );
}
